import { useCallback, useEffect, useState } from 'react'

import { useAppAuth } from '@/lib/auth/client'
import { fetchCustomerBootstrap, isExpectedBootstrapFailure } from '@/lib/customer-bootstrap'
import { ProductTypes } from '@/services/stripe/product-types'
import { checkServiceEntitlement, FULL_SUITE_PRICE_IDS, STANDARD_PACKAGE_PRICE_IDS } from '@/services/stripe/products'
import { isCacheStale, useGeneralStore, type CustomerActiveSubscription } from '@/store/useGeneralStore'

interface ServiceEntitlement {
  hasAccess: boolean
  source: 'package' | 'standalone' | 'none'
  packageType?: 'standard' | 'full_suite'
}

interface UseServiceEntitlementReturn {
  hasAccess: boolean
  source: 'package' | 'standalone' | 'none'
  packageType?: 'standard' | 'full_suite'
  isLoading: boolean
  /**
   * The entitlement lookup could not be completed, so `hasAccess: false` means
   * "unknown", not "no subscription". Callers that gate a purchase flow must
   * fail CLOSED on this rather than offering a duplicate purchase.
   */
  isError: boolean
  activeSubscriptions: string[]
  subscriptionDetails: CustomerActiveSubscription[]
  hasFullSuite: boolean
  hasStandardPackage: boolean
  hasAnyPackage: boolean
  refresh: () => Promise<void>
}

function resolveEntitlement(
  subscriptions: CustomerActiveSubscription[],
  serviceType?: ProductTypes,
): {
  priceIds: string[]
  entitlement: ServiceEntitlement
} {
  const priceIds = subscriptions.map((subscription) => subscription.plan).filter(Boolean)
  const entitlement: ServiceEntitlement = serviceType
    ? checkServiceEntitlement(priceIds, serviceType)
    : { hasAccess: false, source: 'none' }

  return {
    priceIds,
    entitlement,
  }
}

// Services included in the Full Suite package — used to grant access for wire transfer subscribers
const WIRE_TRANSFER_FULL_SUITE_SERVICES = new Set<ProductTypes>([
  ProductTypes.MAILROOM,
  ProductTypes.REGISTERED_AGENT,
  ProductTypes.ACCOUNTING,
  ProductTypes.FEDERAL_TAX,
  ProductTypes.STATE_TAX,
])

/** The bootstrap the shell already read for this user, while it is fresh; otherwise nothing. */
function freshBootstrapCache(userId: string | null | undefined, isLoaded: boolean) {
  if (!isLoaded || !userId) return null
  const cache = useGeneralStore.getState().customerBootstrapCache
  return cache.user?.userId === userId && !isCacheStale(cache.lastFetchedAt) ? cache : null
}

export function useServiceEntitlement(serviceType?: ProductTypes): UseServiceEntitlementReturn {
  const { userId, isLoaded } = useAppAuth()
  const setCustomerBootstrapCache = useGeneralStore((state) => state.setCustomerBootstrapCache)
  // A fresh cached bootstrap answers on the first render, so a page never draws (or fetches for) a
  // loading state it does not have: the effect below would answer the same a render later.
  const [initialCache] = useState(() => freshBootstrapCache(userId, isLoaded))
  const [isLoading, setIsLoading] = useState(initialCache === null)
  const [isError, setIsError] = useState(false)
  const [subscriptionDetails, setSubscriptionDetails] = useState<CustomerActiveSubscription[]>(
    () => initialCache?.activeSubscriptions ?? [],
  )
  const [hasWireTransferFullSuite, setHasWireTransferFullSuite] = useState(
    () => initialCache?.hasWireTransferFullSuite ?? false,
  )
  const [entitlement, setEntitlement] = useState<ServiceEntitlement>(() =>
    initialCache
      ? resolveEntitlement(initialCache.activeSubscriptions, serviceType).entitlement
      : { hasAccess: false, source: 'none' },
  )

  const applySubscriptionState = useCallback(
    (subscriptions: CustomerActiveSubscription[]) => {
      const { entitlement: resolvedEntitlement } = resolveEntitlement(subscriptions, serviceType)
      setSubscriptionDetails(subscriptions)
      setEntitlement((prev) => {
        if (
          prev.hasAccess === resolvedEntitlement.hasAccess &&
          prev.source === resolvedEntitlement.source &&
          prev.packageType === resolvedEntitlement.packageType
        ) {
          return prev
        }
        return resolvedEntitlement
      })
      setIsLoading(false)
    },
    [serviceType],
  )

  const loadSubscriptions = useCallback(
    async (forceRefresh = false) => {
      if (!isLoaded) {
        return
      }

      setIsError(false)

      if (!userId) {
        applySubscriptionState([])
        return
      }

      const cache = freshBootstrapCache(userId, isLoaded)
      if (!forceRefresh && cache) {
        setHasWireTransferFullSuite(cache.hasWireTransferFullSuite)
        applySubscriptionState(cache.activeSubscriptions)
        return
      }

      try {
        setIsLoading(true)
        const payload = await fetchCustomerBootstrap(userId, forceRefresh)

        if (!payload.user) {
          throw new Error('Customer bootstrap payload is missing user data')
        }

        const wireTransferFullSuite = payload.hasWireTransferFullSuite ?? false
        setHasWireTransferFullSuite(wireTransferFullSuite)

        setCustomerBootstrapCache({
          user: payload.user,
          onboardingStatus: payload.onboardingStatus,
          activeSubscriptions: payload.activeSubscriptions || [],
          hasWireTransferFullSuite: wireTransferFullSuite,
        })

        applySubscriptionState(payload.activeSubscriptions || [])
      } catch (error) {
        // console.error is a Sentry report here (captureConsoleIntegration), so
        // an expired session became an error-level issue (NONRESIDENT-DW).
        if (!isExpectedBootstrapFailure(error)) {
          console.error('[useServiceEntitlement] Failed to load customer bootstrap', error)
        }
        // Swallowing the failure into an empty subscription list is not a "no
        // entitlement" answer — it is no answer. Flag it so purchase surfaces
        // can fail closed instead of offering a duplicate purchase.
        setIsError(true)
        applySubscriptionState([])
      }
    },

    [applySubscriptionState, isLoaded, setCustomerBootstrapCache, userId],
  )

  useEffect(() => {
    void loadSubscriptions()
  }, [loadSubscriptions])

  const activeSubscriptions = subscriptionDetails.map((subscription) => subscription.plan)
  const hasFullSuite = activeSubscriptions.some((id) => (FULL_SUITE_PRICE_IDS as readonly string[]).includes(id))
  const hasStandardPackage = activeSubscriptions.some((id) =>
    (STANDARD_PACKAGE_PRICE_IDS as readonly string[]).includes(id),
  )

  // Wire transfer Full Suite grants access to the same services as a Stripe Full Suite subscription
  const wireTransferGrantsAccess =
    hasWireTransferFullSuite && serviceType !== undefined && WIRE_TRANSFER_FULL_SUITE_SERVICES.has(serviceType)
  const finalHasAccess = entitlement.hasAccess || wireTransferGrantsAccess
  const effectiveSource: 'package' | 'standalone' | 'none' =
    finalHasAccess && !entitlement.hasAccess ? 'package' : entitlement.source
  const effectivePackageType: 'standard' | 'full_suite' | undefined =
    finalHasAccess && !entitlement.hasAccess ? 'full_suite' : entitlement.packageType

  return {
    hasAccess: finalHasAccess,
    source: effectiveSource,
    packageType: effectivePackageType,
    isLoading,
    isError,
    activeSubscriptions,
    subscriptionDetails,
    hasFullSuite: hasFullSuite || hasWireTransferFullSuite,
    hasStandardPackage,
    hasAnyPackage: hasFullSuite || hasStandardPackage || hasWireTransferFullSuite,
    refresh: () => loadSubscriptions(true),
  }
}
